import { all, call, fork, put, select, takeEvery } from "redux-saga/effects";

// Login Redux States
import {
  apiError,
  loginSuccess,
  logoutUserSuccess,
} from "../../actions/authentication";
import {
  LOGIN_USER,
  LOGIN_ANONYMOUS,
  LOGOUT_USER,
} from "../../constants/actionTypes";

//Include Both Helper File with needed methods
import { getFirebaseBackend } from "../../helpers/firebase_helper";
import { clearPrimaryLocationSession } from "../../actions/user";
import { loginTenantUser } from "../../api/user";
import {
  handheldPinLoginEnv,
  shouldUseHandheldPinLogin,
} from "../../helpers/handheldPin";
import {
  isHandheldStaffUser,
  writeScannerHomePref,
} from "../../helpers/handheldMode";
import { loginFailureMessage } from "../../helpers/loginError";

const fireBaseBackend = getFirebaseBackend();

function* loginUser({ payload: { user } }) {
  try {
    // Force primary-location snap on the upcoming boot requestCurrentUser.
    clearPrimaryLocationSession(user?.uid);
    let customToken = user.customToken;
    if (!customToken && user.email && user.password) {
      const res = yield call(loginTenantUser, {
        email: user.email,
        password: user.password,
      });
      customToken = res?.data?.customToken || res?.customToken;
      if (!customToken) {
        throw new Error("Could not sign in. Try again.");
      }
    }
    const response = yield call(
      fireBaseBackend.loginUser,
      user.email,
      user.password,
      customToken
    );

    yield put(loginSuccess(response));
    // Callers (e.g. the onboarding "Build my store" hand-off, or Register
    // pairing "I'm an admin…") can request a post-login destination.
    let stored = "";
    try {
      stored = sessionStorage.getItem("auth.redirectTo") || "";
      sessionStorage.removeItem("auth.redirectTo");
    } catch {
      /* private mode */
    }
    const dest = String(user.redirectTo || stored || "").trim();
    const safe =
      dest.startsWith("/") && !dest.startsWith("//") && !dest.includes("://")
        ? dest
        : "/dashboard";
    window.location.href = safe;
  } catch (error) {
    yield put(apiError(loginFailureMessage(error)));
  }
}

function* logoutUser() {
  try {
    const user = yield select((s) => s?.currentUser?.user);
    clearPrimaryLocationSession();
    try {
      if (typeof sessionStorage !== "undefined") {
        Object.keys(sessionStorage)
          .filter((k) => k.startsWith("riverr.primaryLocationApplied:"))
          .forEach((k) => sessionStorage.removeItem(k));
      }
    } catch (_) {
      /* ignore */
    }
    const response = yield call(fireBaseBackend.logout);
    yield put(logoutUserSuccess(response));
    const stayOnPin =
      isHandheldStaffUser(user) &&
      shouldUseHandheldPinLogin({ ...handheldPinLoginEnv(), user });
    if (stayOnPin) {
      writeScannerHomePref("on");
      window.location.href = "/handheld";
    } else {
      window.location.href = "/login";
    }
  } catch (error) {
    yield put(apiError(error.message));
  }
}

function* loginAnonymous() {
  try {
    const response = yield call(fireBaseBackend.loginAnonymously);
    yield put(loginSuccess(response));
  } catch (error) {
    yield put(apiError(error.message));
  }
}

export function* watchUserLogin() {
  yield takeEvery(LOGIN_USER, loginUser);
}

export function* watchAnonymousLogin() {
  yield takeEvery(LOGIN_ANONYMOUS, loginAnonymous);
}

export function* watchUserLogout() {
  yield takeEvery(LOGOUT_USER, logoutUser);
}

function* authSaga() {
  yield all([
    fork(watchUserLogin),
    fork(watchUserLogout),
    fork(watchAnonymousLogin),
  ]);
}

export default authSaga;
